/**
 * Matches "hidden" Unicode characters that are typically introduced via
 * copy/paste and are visually invisible or formatting-related.
 *
 * What this catches:
 * - \p{Cf} (Unicode "Format" characters), including:
 *   - Zero-width space (U+200B)
 *   - Zero-width joiner (U+200D)
 *   - Word joiner (U+2060)
 *   - Bidirectional control characters (U+202A-U+202E, U+2066-U+2069)
 *   - BOM / zero-width no-break space (U+FEFF)
 * - Unicode spacing separators commonly introduced by copy/paste:
 *   - Non-breaking space (U+00A0)
 *   - Ogham space mark (U+1680)
 *   - En/em/figure/thin/hair spaces (U+2000-U+200A)
 *   - Narrow no-break space (U+202F)
 *   - Medium mathematical space (U+205F)
 *   - Ideographic space (U+3000)
 *
 * What this intentionally does NOT catch:
 * - Newlines (\n, \r)
 * - Tabs (\t)
 * - Normal space (U+0020)
 * - Visible characters (letters, numbers, punctuation, symbols)
 */
const HIDDEN_UNICODE_PATTERN =
  '[\\p{Cf}\\u00A0\\u1680\\u2000-\\u200A\\u202F\\u205F\\u3000]';
const HIDDEN_UNICODE_REGEX = new RegExp(HIDDEN_UNICODE_PATTERN, 'gu');
const HIDDEN_UNICODE_SINGLE_CHARACTER_REGEX = new RegExp(
  HIDDEN_UNICODE_PATTERN,
  'u',
);

export const getHiddenUnicodeCharacters = (value: string): string[] =>
  value.match(HIDDEN_UNICODE_REGEX) ?? [];

export const hasHiddenUnicodeCharacters = (value: string): boolean =>
  getHiddenUnicodeCharacters(value).length > 0;

export const removeHiddenUnicodeCharacters = (value: string): string =>
  value.replace(HIDDEN_UNICODE_REGEX, '');

export const formatHiddenUnicodeCodePoint = (character: string): string =>
  `U+${character.codePointAt(0)?.toString(16).toUpperCase().padStart(4, '0')}`;

export interface VisibleHiddenUnicodePart {
  value: string;
  isHiddenUnicode: boolean;
  position: number;
}

export const getVisibleHiddenUnicodeParts = (
  value: string,
): VisibleHiddenUnicodePart[] => {
  let position = 0;

  return Array.from(value).map((character) => {
    const part = HIDDEN_UNICODE_SINGLE_CHARACTER_REGEX.test(character)
      ? {
          value: formatHiddenUnicodeCodePoint(character),
          isHiddenUnicode: true,
          position,
        }
      : {
          value: character,
          isHiddenUnicode: false,
          position,
        };

    position += character.length;
    return part;
  });
};
